import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ActiveDeployment, DeploymentResult } from '../../../models/Deployment';
import { Project } from '../../../models/Project';
import { SiteBatch, RunningSite } from '../../../models/SiteBatch';
import {
  useActiveDeployment,
  ACTIVE_DEPLOYMENT_QUERY_KEY,
} from '../../../hooks/useActiveDeployment';
import { useDeploymentPolling } from '../../../hooks/useDeploymentPolling';
import { useActiveBatches } from '../../../hooks/useActiveBatches';
import { useRunningSites } from '../../../hooks/useRunningSites';

import { useSiteManagement } from './useSiteManagement';
import { useRecentDeployments, RecentDeploymentItem } from './useRecentDeployments';
import { useRecentFailures } from './useRecentFailures';
import type { SiteRowData } from '../widgets/SiteRow';
import type { SortKey, SortDir, KpiFilter } from '../types';

const RECENTLY_COMPLETED_WINDOW_MS = 2 * 60 * 1000;
const VALID_FILTERS: readonly KpiFilter[] = ['all', 'running', 'deploying', 'failed'] as const;

const activeToDeploymentResult = (a: ActiveDeployment): DeploymentResult => ({
  id: a.deploymentId,
  project_id: a.projectId,
  version: '',
  status: a.status,
  deployed_by: '',
  deployed_at: a.deployedAt,
  environment: 'production',
  branch: a.branch,
  commit_hash: '',
  description: '',
  logs: '',
  created_at: a.deployedAt,
  updated_at: a.deployedAt,
});

export interface UseSiteDashboardResult {
  /** KPI 데이터. */
  kpi: {
    total: number;
    running: number;
    deploying: number;
    failures24h: number;
    loading: {
      total: boolean;
      running: boolean;
      deploying: boolean;
      failures: boolean;
    };
  };
  /** Live Deployments 위젯용 */
  live: {
    activeBatches: SiteBatch[];
    runningSites: RunningSite[];
    sitesByName: Map<string, Project>;
  };
  /** Timeline 위젯용 */
  recent: RecentDeploymentItem[];
  recentLoading: boolean;
  /** SiteGrid rows (모든 site 결합, filter 미적용) */
  rows: SiteRowData[];

  // 검색/페이징/정렬 (기존 useSiteManagement 노출)
  searchTerm: string;
  onSearchChange: (value: string) => void;
  listLoading: boolean;
  listError: string | null;
  refresh: () => void;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (next: number) => void;
  sortBy: SortKey;
  sortDir: SortDir;
  onSortChange: (key: SortKey) => void;

  // KPI 필터 (URL 쿼리 ?filter=...)
  filter: KpiFilter;
  setFilter: (next: KpiFilter) => void;

  // Create Site Dialog
  createOpen: boolean;
  openCreate: () => void;
  closeCreate: () => void;
  onCreateSaved: (message: string) => void;

  // site-edit-delete-ui: 편집 다이얼로그 (CreateSiteDialog 재사용)
  editDialogSite: Project | null;
  openEdit: (site: Project) => void;
  closeEdit: () => void;
  onEditSaved: (message: string) => void;

  // site-edit-delete-ui: 삭제 다이얼로그
  deleteDialogSite: Project | null;
  openDelete: (site: Project) => void;
  closeDelete: () => void;
  onDeleted: (message: string) => void;

  // Snackbar pass-through (CreateSiteDialog, etc.)
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
  dismissSnackbar: () => void;

  // Navigation helpers
  navigateToMap: (site: Project) => void;
}

/**
 * 대시보드 통합 합성 훅.
 *
 * 기존 개별 hook들을 조합하여 SiteDashboardView 가 한 곳에서 데이터를
 * 가져올 수 있도록 한다. 각 훅의 특성은 건드리지 않고 표면만 정리.
 *
 * - 검색/페이징은 useSiteManagement 그대로 위임
 * - 3개 폴링 훅(active-deployment/active-batches/running-sites) 은
 *   기존 staleTime/interval 유지
 * - 추가 2개(recent/failures) 는 dashboard 전용
 * - filter state 는 URL ?filter=... 쿼리와 동기화
 */
export default function useSiteDashboard(): UseSiteDashboardResult {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  // Filter from URL
  const rawFilter = params.get('filter');
  const filter: KpiFilter = (VALID_FILTERS as readonly string[]).includes(rawFilter ?? '')
    ? (rawFilter as KpiFilter)
    : 'all';

  const setFilter = useCallback(
    (next: KpiFilter) => {
      const p = new URLSearchParams(params);
      if (next === 'all') p.delete('filter');
      else p.set('filter', next);
      setParams(p, { replace: true });
    },
    [params, setParams],
  );

  // Base hooks
  const siteMgmt = useSiteManagement();
  const { data: activeRes, isLoading: activeLoading } = useActiveDeployment();
  const active: ActiveDeployment | null = activeRes?.data ?? null;

  const isActiveInProgress = active?.status === 'in_progress';
  useDeploymentPolling(active?.projectId ?? null, { enabled: isActiveInProgress });

  const anySiteId = siteMgmt.pagedSites[0]?.id ?? null;
  const { data: activeBatchesRaw, isLoading: batchesLoading } = useActiveBatches(anySiteId, {
    enabled: !!anySiteId,
  });
  const { data: runningSitesRaw, isLoading: runningLoading } = useRunningSites(anySiteId, {
    enabled: !!anySiteId,
  });
  const { data: recentRaw, isLoading: recentLoading } = useRecentDeployments(anySiteId, {
    enabled: !!anySiteId,
    limit: 10,
  });
  const { data: failuresRaw, isLoading: failuresLoading } = useRecentFailures(anySiteId, {
    enabled: !!anySiteId,
  });

  // Active state transition refresh (from DeployPanel)
  const previousStatusRef = useRef<string | null>(null);
  useEffect(() => {
    const polled = queryClient.getQueryData<{ data?: DeploymentResult }>([
      'deployment',
      'latest',
      active?.projectId ?? null,
    ]);
    const current = polled?.data?.status ?? active?.status ?? null;
    if (
      previousStatusRef.current === 'in_progress' &&
      current &&
      current !== 'in_progress'
    ) {
      queryClient.invalidateQueries({ queryKey: ACTIVE_DEPLOYMENT_QUERY_KEY });
      siteMgmt.refresh();
    }
    previousStatusRef.current = current;
  });

  // Indexes
  const activeBatchesBySite = useMemo(() => {
    const map = new Map<string, SiteBatch>();
    (activeBatchesRaw ?? []).forEach((b) => map.set(b.site, b));
    return map;
  }, [activeBatchesRaw]);

  const runningSitesBySite = useMemo(() => {
    const map = new Map<string, RunningSite>();
    (runningSitesRaw ?? []).forEach((r) => map.set(r.site, r));
    return map;
  }, [runningSitesRaw]);

  const sitesByName = useMemo(() => {
    const map = new Map<string, Project>();
    siteMgmt.sites.forEach((s) => map.set(s.name, s));
    return map;
  }, [siteMgmt.sites]);

  // Row composition
  const rows: SiteRowData[] = useMemo(() => {
    const now = Date.now();
    return siteMgmt.pagedSites.map((site) => {
      const legacyActive = !!active && site.id === active.projectId;
      const legacyInProgress = legacyActive && active?.status === 'in_progress';
      const rawActiveBatch = activeBatchesBySite.get(site.name);
      const running = runningSitesBySite.get(site.name);
      const activeBatch =
        rawActiveBatch && (rawActiveBatch.current_step ?? 0) > 0 ? rawActiveBatch : null;
      const isBatchRunningLive = !!running && !activeBatch;
      const finishedAt = running?.finished_at ? new Date(running.finished_at).getTime() : null;
      const recentlyCompleted =
        !!finishedAt && !activeBatch && now - finishedAt < RECENTLY_COMPLETED_WINDOW_MS;

      return {
        site,
        lastDeployment: legacyActive && active ? activeToDeploymentResult(active) : null,
        isActive: (legacyActive && !legacyInProgress) || isBatchRunningLive,
        isInProgress: !!legacyInProgress,
        activeBatchId: activeBatch?.batch_id ?? running?.batch_id ?? null,
        activeCurrentStep: activeBatch?.current_step ?? null,
        activeTotalSteps: activeBatch?.total_steps ?? null,
        recentlyCompleted,
      };
    });
  }, [siteMgmt.pagedSites, active, activeBatchesBySite, runningSitesBySite]);

  // KPI
  const deployingCount = (activeBatchesRaw ?? []).filter((b) => (b.current_step ?? 0) > 0).length;

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  // site-edit-delete-ui: 편집/삭제 다이얼로그 대상 사이트. null 이면 닫힘.
  const [editDialogSite, setEditDialogSite] = useState<Project | null>(null);
  const [deleteDialogSite, setDeleteDialogSite] = useState<Project | null>(null);
  const [snackbar, setSnackbar] = useState<UseSiteDashboardResult['snackbar']>({
    open: false,
    message: '',
    severity: 'success',
  });

  const openCreate = useCallback(() => setCreateOpen(true), []);
  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const dismissSnackbar = useCallback(
    () => setSnackbar((s) => ({ ...s, open: false })),
    [],
  );

  const onCreateSaved = useCallback(
    (message: string) => {
      setSnackbar({ open: true, message, severity: 'success' });
      siteMgmt.refresh();
    },
    [siteMgmt],
  );

  // site-edit-delete-ui: 편집 다이얼로그 (CreateSiteDialog + initialProject)
  const openEdit = useCallback((site: Project) => setEditDialogSite(site), []);
  const closeEdit = useCallback(() => setEditDialogSite(null), []);
  const onEditSaved = useCallback(
    (message: string) => {
      setEditDialogSite(null);
      setSnackbar({ open: true, message, severity: 'success' });
      siteMgmt.refresh();
    },
    [siteMgmt],
  );

  // site-edit-delete-ui: 삭제 다이얼로그
  const openDelete = useCallback((site: Project) => setDeleteDialogSite(site), []);
  const closeDelete = useCallback(() => setDeleteDialogSite(null), []);
  const onDeleted = useCallback(
    (message: string) => {
      setDeleteDialogSite(null);
      setSnackbar({ open: true, message, severity: 'success' });
      siteMgmt.refresh();
    },
    [siteMgmt],
  );

  const navigateToMap = useCallback(
    (site: Project) => {
      try {
        localStorage.setItem('selectedProjectId', site.id);
        localStorage.setItem('selectedProject', JSON.stringify(site));
      } catch {
        /* ignore storage errors */
      }
      navigate(`/project/${site.id}`);
    },
    [navigate],
  );

  return {
    kpi: {
      total: siteMgmt.sites.length,
      running: (runningSitesRaw ?? []).length,
      deploying: deployingCount,
      failures24h: failuresRaw?.count ?? 0,
      loading: {
        total: siteMgmt.loading,
        running: runningLoading,
        deploying: batchesLoading || activeLoading,
        failures: failuresLoading,
      },
    },
    live: {
      activeBatches: activeBatchesRaw ?? [],
      runningSites: runningSitesRaw ?? [],
      sitesByName,
    },
    recent: recentRaw ?? [],
    recentLoading,
    rows,

    searchTerm: siteMgmt.searchTerm,
    onSearchChange: siteMgmt.onSearchChange,
    listLoading: siteMgmt.loading,
    listError: siteMgmt.error,
    refresh: siteMgmt.refresh,
    page: siteMgmt.page,
    pageSize: siteMgmt.pageSize,
    totalItems: siteMgmt.totalItems,
    onPageChange: siteMgmt.onPageChange,
    sortBy: siteMgmt.sortBy,
    sortDir: siteMgmt.sortDir,
    onSortChange: siteMgmt.onSortChange,

    filter,
    setFilter,

    createOpen,
    openCreate,
    closeCreate,
    onCreateSaved,

    editDialogSite,
    openEdit,
    closeEdit,
    onEditSaved,

    deleteDialogSite,
    openDelete,
    closeDelete,
    onDeleted,

    snackbar,
    dismissSnackbar,

    navigateToMap,
  };
}
